import { AfterViewInit, ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map, Observable } from 'rxjs';
import { GoalActionEditComponent } from 'src/app/components';
import { GoalInformationDialogComponent } from 'src/app/dialogs/goal-information-dialog/goal-information-dialog.component';
import { GoalInformation } from 'src/app/enums/goal-information.enum';
import { GoalPeriodStatus } from 'src/app/enums/goal-period-status.enum';
import { GoalTypeEnum } from 'src/app/enums/goal-type.enum';
import { GoalPermissionsHelper } from 'src/app/helpers/goal-permissions.helper';
import { ActionModel } from 'src/app/models/action.model';
import { Employee } from 'src/app/models/employee.model';
import { IResultModel, ResultModel } from 'src/app/models/general/result.model';
import { GoalPage } from 'src/app/models/goal-page.model';
import { GoalModel} from 'src/app/models/goal.model';
import { GroupGoal } from 'src/app/models/group-goal.model';
import { GroupGoalsData } from 'src/app/models/group-goals-data.model';
import { State } from 'src/app/models/state.model';
import { GoalService, StateService } from 'src/app/services';
import { ErrorMessageService } from 'src/app/shared/services';
import { DataTypeHelper } from 'src/app/shared/helpers/data-type.helper';

@UntilDestroy()
@Component({
	selector: 'app-text-based-goal',
	templateUrl: './text-based-goal.component.html',
	styleUrl: './text-based-goal.component.scss'
})
export class TextBasedGoalComponent implements  OnInit, AfterViewInit {
	public leaders: Employee[] = [];
	public loading = false;

	public data: GoalPage;
	public mainForm: FormGroup;

	public groupGoalData: GroupGoalsData;

	public state: State;

  	@ViewChildren(GoalActionEditComponent) goalActionComponents!: QueryList<GoalActionEditComponent>;

	public get isAllChildValid(): boolean {
		if (!this.goalActionComponents || this.goalActionComponents.length === 0) return true;

		return this.goalActionComponents.toArray().every(c => { 
			return c.mainForm.valid; 
		});
	}

	public get isSelf(): boolean {
		return this.state?.me.value?.employee?.id === this.data?.employeeId;
	}

	constructor(
		private router: Router,
		private fb: FormBuilder,
		private route: ActivatedRoute,
		private goalService: GoalService,
        private msgService: ErrorMessageService,
		stateService: StateService,
		private cd: ChangeDetectorRef,
		public dialog: MatDialog
	) {
		this.state = stateService.state;
	}

	ngOnInit() {
		this.mainForm = this.createForm();

		this.route.params.pipe(untilDestroyed(this)).subscribe((params) => {
			const goalId = params['goalId'] ? parseInt(params['goalId']) : null;
			const goalPeriodId = params['goalPeriodId'] ? parseInt(params['goalPeriodId']) : null;

			const obs: Observable<GoalPage> = goalId
      			? this.goalService.getGoal(goalId)
      			: this.goalService.getNewGoal(goalPeriodId, GoalTypeEnum.TextBasedGoal);

			if (!obs) {
				this.loading = false;
				return;
			}

			obs.pipe(untilDestroyed(this)).subscribe({
				next: (data: GoalPage) => {
					this.data = data;
					this.groupGoalData = new GroupGoalsData();
					this.groupGoalData.goalPeriodId = this.data.goalPeriod.id;
					this.groupGoalData.isSelf = this.isSelf;
					this.groupGoalData.groupGoals = this.data.leaderGroupGoals ?? [];

					this.mainForm.patchValue(this.data.goal, { emitEvent: false });
					this.setFormControlState();
					this.cd.detectChanges(); // Trigger change detection

					this.loading = false;
				},
				error: (e) => {
					this.loading = false;
				}
			});
		});
	}

	ngAfterViewInit() {
		this.goalActionComponents.changes
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.cd.detectChanges();
			});
	}

	private createForm(): FormGroup {
		return this.fb.group({
			id: 0,

			goalTextId: null,
			
			groupGoalId: null,
            goalPeriodId: null,
            targetValue: null,
            goalType: null,
            result: null,
            weight: [null, [Validators.min(1), Validators.max(100), Validators.pattern(/^\d+$/) ]]
		});
	}

	public setFormControlState() {
		if (this.state.isAdmin || this.state.isSuperAdmin) return;

		const isAllowEditWeight = GoalPermissionsHelper.canEditWeight(this.data?.goalPeriod, this.state);
		if (!isAllowEditWeight) {
			this.mainForm.get('weight')?.disable({ emitEvent: false });
		}
	}

	public isAllowEditForm(): boolean {
		return GoalPermissionsHelper.canGoalFormEdit(this.data?.goalPeriod, this.state);
	}

	public allowAddNew(): boolean {
		return GoalPermissionsHelper.canGoalActionAddNew(this.data?.goalPeriod, this.state);
	}

	public allowOk(): boolean {
    	return !this.mainForm.invalid && GoalPermissionsHelper.canGoalActionSave(this.data?.goalPeriod, this.state);
  	}

	getBackLink(): string {
		return `/goals/${this.data.reviewPeriodId}/${this.data.employeeId}`;
	}

	//delete from the local array when id is null, not delete when id is set
	onActionDelete(index: number): void {
        this.data.goal.actions.splice(index, 1);
	}

	onGroupGoalChange(event: any): void {}

	onNewAction(): void {
		const action = new ActionModel();
		this.data.goal.actions.push(action);
	}

	saveGoal(close: boolean) {
		const goal = this.mainForm.getRawValue() as GoalModel;
		goal.actions = [];
		goal.kpiList = [];
		goal.groupGoalId = this.data.goal.groupGoalId;

		for (const c of this.goalActionComponents) {

			if (c.mainForm.invalid)
			{
				c.mainForm.markAllAsTouched();
				this.msgService.showSnackBarMessage('fixTheIncorrectFieldsForSave');
				return;
			}

			const actionRaw = c.mainForm.getRawValue();
			let action = actionRaw as ActionModel;
			action.weight = DataTypeHelper.parseToNumber(actionRaw.weight);
			action.minExpectation = DataTypeHelper.parseToNumber(actionRaw.minExpectation);
			action.comments = c.action.comments;
			action.progress = c.action.progress;
			goal.actions.push(action);
		}

		this.goalService.saveGoal(goal)
			.pipe(untilDestroyed(this))
			.subscribe({
				next: (result) => {
					this.loading = false;

					if (close)
						this.router.navigate([this.getBackLink()]);

					this.msgService.showSnackBarMessage('saveSuccessful'); 
				},
				error: (err: IResultModel<ResultModel>) => {
					this.loading = false;
                    this.msgService.handleError<ResultModel>(err);
				}
		    }
        );
	}

	onOk(close: boolean = true): void {
		if (this.mainForm.invalid) {
			return;
		}

		this.loading = true;

		this.saveGoal(close);
	}

	groupGoalChanged(groupGoal: GroupGoal) {
		if (!groupGoal)
		{
			this.data.goal.groupGoalId = null;
		}
		else
		{
			this.data.goal.groupGoalId = groupGoal.id;
		}
	}

	showInformation(): void {
		this.dialog.open(GoalInformationDialogComponent, {
				width: "850px",
				data: { goalInfoType: GoalInformation.TextBasedGoal }
		});
	}
}
